import { useEffect } from 'react';

const SEO = ({ 
  title, 
  description, 
  keywords, 
  ogTitle, 
  ogDescription, 
  ogImage,
  canonicalUrl,
  structuredData,
  faqData,
  pageType = 'website'
}) => {
  useEffect(() => {
    // Default SEO values
    const defaultTitle = 'Vitta Finance Academy - CIRO Exam Prep | Canada\'s #1 CIRE, RSE, ISE Courses';
    const defaultDescription = 'Pass your CIRO exams with confidence. Expert-led prep courses for CIRE, RSE, ISE, and Derivatives exams. 30,000+ successful learners, industry leading pass rate. AI tutor, mock exams, 700+ page study guides. Canada\'s trusted exam prep provider since 2010.';
    const defaultKeywords = 'CIRO exam, CIRO exams, CIRO proficiency exam, CIRO licensing exam, Canadian Investment Regulatory Organization exam, CIRE exam, CIRE exam prep, RSE exam, Retail Securities Exam, ISE exam, Institutional Securities Exam, Derivatives exam, CIRO study guide, CIRO practice exam, investment dealer licensing Canada, securities licensing Canada, registered representative Canada, CIRO exam hub, CIRO exam syllabus, pass CIRE exam, CIRO proficiency model 2026, IIROC exam, MFDA exam';

    // Set document title
    document.title = title || defaultTitle;

    // Helper function to set/update meta tags
    const setMetaTag = (name, content, isProperty = false) => {
      const selector = isProperty ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let tag = document.querySelector(selector);
      if (tag) {
        tag.setAttribute('content', content);
      } else {
        tag = document.createElement('meta');
        tag.setAttribute(isProperty ? 'property' : 'name', name);
        tag.setAttribute('content', content);
        document.head.appendChild(tag);
      }
    };

    // Basic meta tags
    setMetaTag('description', description || defaultDescription);
    setMetaTag('keywords', keywords || defaultKeywords);
    
    // Additional SEO meta tags
    setMetaTag('author', 'Vitta Finance Academy');
    setMetaTag('robots', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    setMetaTag('googlebot', 'index, follow');
    setMetaTag('language', 'English');
    setMetaTag('revisit-after', '7 days');
    setMetaTag('distribution', 'global');
    setMetaTag('rating', 'general');
    
    // Geo tags for Canada
    setMetaTag('geo.region', 'CA-AB');
    setMetaTag('geo.placename', 'Alberta, Canada');
    setMetaTag('geo.position', '53.9333;-116.5765');
    setMetaTag('ICBM', '53.9333, -116.5765');

    // Open Graph tags
    setMetaTag('og:title', ogTitle || title || defaultTitle, true);
    setMetaTag('og:description', ogDescription || description || defaultDescription, true);
    setMetaTag('og:type', pageType, true);
    setMetaTag('og:site_name', 'Vitta Finance Academy', true);
    setMetaTag('og:locale', 'en_CA', true);
    if (ogImage) {
      setMetaTag('og:image', ogImage, true);
      setMetaTag('og:image:alt', 'Vitta Finance Academy - CIRO Exam Prep', true);
    }
    if (canonicalUrl) {
      setMetaTag('og:url', canonicalUrl, true);
    }

    // Twitter Card tags
    setMetaTag('twitter:card', 'summary_large_image');
    setMetaTag('twitter:title', ogTitle || title || defaultTitle);
    setMetaTag('twitter:description', ogDescription || description || defaultDescription);
    if (ogImage) {
      setMetaTag('twitter:image', ogImage);
    }

    // Canonical URL
    if (canonicalUrl) {
      let link = document.querySelector('link[rel="canonical"]');
      if (link) {
        link.setAttribute('href', canonicalUrl);
      } else {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        link.setAttribute('href', canonicalUrl);
        document.head.appendChild(link);
      }
    }

    // Remove existing structured data scripts
    const existingScripts = document.querySelectorAll('script[data-seo]');
    existingScripts.forEach(s => s.remove());

    // Default Organization structured data
    const defaultStructuredData = {
      "@context": "https://schema.org",
      "@type": "EducationalOrganization",
      "name": "Vitta Finance Academy",
      "alternateName": ["Vitta Finance", "VittaFinance", "Vitta Finance Academy Inc."],
      "url": "https://www.vittafinance.ca",
      "logo": "https://www.vittafinance.ca/logo.jpg",
      "description": defaultDescription,
      "foundingDate": "2010",
      "address": {
        "@type": "PostalAddress",
        "addressRegion": "Alberta",
        "addressCountry": "CA"
      },
      "contactPoint": {
        "@type": "ContactPoint",
        "email": "info@vittafinance.ca",
        "contactType": "customer service"
      },
      "sameAs": [
        "https://www.linkedin.com/in/amina-beecroft/",
        "https://www.youtube.com/@FinanceinCanada"
      ],
      "areaServed": {
        "@type": "Country",
        "name": "Canada"
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "reviewCount": "2500",
        "bestRating": "5"
      },
      "hasOfferingCatalog": {
        "@type": "OfferingCatalog",
        "name": "CIRO Exam Prep Courses",
        "itemListElement": [
          {
            "@type": "Course",
            "name": "CIRE Exam Prep Course",
            "description": "Comprehensive Canadian Investment Regulatory Exam preparation",
            "provider": {"@type": "Organization", "name": "Vitta Finance Academy"},
            "offers": {"@type": "Offer", "price": "29.99", "priceCurrency": "CAD"}
          },
          {
            "@type": "Course",
            "name": "RSE Exam Prep Course",
            "description": "Retail Securities Exam preparation for investment advisors",
            "provider": {"@type": "Organization", "name": "Vitta Finance Academy"},
            "offers": {"@type": "Offer", "price": "29.99", "priceCurrency": "CAD"}
          },
          {
            "@type": "Course",
            "name": "ISE Exam Prep Course",
            "description": "Institutional Securities Exam preparation",
            "provider": {"@type": "Organization", "name": "Vitta Finance Academy"},
            "offers": {"@type": "Offer", "price": "29.99", "priceCurrency": "CAD"}
          },
          {
            "@type": "Course",
            "name": "Derivatives Exam Prep Course",
            "description": "Options, futures, and derivatives exam preparation",
            "provider": {"@type": "Organization", "name": "Vitta Finance Academy"},
            "offers": {"@type": "Offer", "price": "29.99", "priceCurrency": "CAD"}
          }
        ]
      }
    };

    // Instructor Person schemas
    const instructorSchemas = [
      {
        "@context": "https://schema.org",
        "@type": "Person",
        "name": "Dr. Amina Beecroft",
        "jobTitle": "Lead Instructor & Founder",
        "honorificPrefix": "Dr.",
        "honorificSuffix": "PhD, CFA, MBA, D.Fin",
        "description": "Finance executive with 30+ years experience in investment banking, portfolio management, and academia. Expert in CIRO exam preparation.",
        "worksFor": {
          "@type": "EducationalOrganization",
          "name": "Vitta Finance Academy"
        },
        "alumniOf": [
          {"@type": "CollegeOrUniversity", "name": "Chartered Financial Analyst Institute"}
        ],
        "sameAs": "https://www.linkedin.com/in/amina-beecroft/",
        "knowsAbout": ["CIRO Exams", "Investment Banking", "Portfolio Management", "Securities Regulation", "Canadian Finance"]
      },
      {
        "@context": "https://schema.org",
        "@type": "Person",
        "name": "Harry Vadalkar",
        "jobTitle": "Lead Instructor & Co-Author",
        "honorificSuffix": "CFA, CSC, MBA",
        "description": "Finance educator who has trained 200,000+ investors and 30,000+ finance professionals. TEDx speaker, teaches at SAIT, MRU, and TRU.",
        "worksFor": [
          {"@type": "EducationalOrganization", "name": "Vitta Finance Academy"},
          {"@type": "CollegeOrUniversity", "name": "SAIT"},
          {"@type": "CollegeOrUniversity", "name": "Mount Royal University"},
          {"@type": "CollegeOrUniversity", "name": "Thompson Rivers University"}
        ],
        "sameAs": ["https://www.linkedin.com/in/herambvadalkar/", "https://www.harryvadalkar.ca"],
        "knowsAbout": ["CIRO Exams", "FinTech", "Blockchain", "Investment Education", "Canadian Securities"]
      }
    ];

    // Add instructor schemas
    instructorSchemas.forEach((schema, idx) => {
      const instructorScript = document.createElement('script');
      instructorScript.type = 'application/ld+json';
      instructorScript.setAttribute('data-seo', `instructor-${idx}`);
      instructorScript.textContent = JSON.stringify(schema);
      document.head.appendChild(instructorScript);
    });

    // Add main structured data
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo', 'structured-data');
    script.textContent = JSON.stringify(structuredData || defaultStructuredData);
    document.head.appendChild(script);

    // Add FAQ structured data if provided
    if (faqData && faqData.length > 0) {
      const faqStructuredData = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqData.map(faq => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer
          }
        }))
      };
      
      const faqScript = document.createElement('script');
      faqScript.type = 'application/ld+json';
      faqScript.setAttribute('data-seo', 'faq-data');
      faqScript.textContent = JSON.stringify(faqStructuredData);
      document.head.appendChild(faqScript);
    }

    // Cleanup
    return () => {
      const scriptsToRemove = document.querySelectorAll('script[data-seo]');
      scriptsToRemove.forEach(s => s.remove());
    };
  }, [title, description, keywords, ogTitle, ogDescription, ogImage, canonicalUrl, structuredData, faqData, pageType]);

  return null;
};

export default SEO;
